import * as React from 'react';
import { AuthContextProps } from 'react-oidc-context';
import { useParams } from 'react-router';

import {
  CdrVersionTier,
  ConfigResponse,
  CreateNewUserSatisfactionSurvey,
  Profile,
} from 'generated/fetch';

import { BreadcrumbType } from 'app/lab/components/breadcrumb-type';
import { profileApi } from 'app/services/swagger-fetch-clients';
import { Atom, atom } from 'app/utils/subscribable';
import StackdriverErrorReporter from 'stackdriver-errors-js';

const { useEffect, useState } = React;

export interface RouteDataStore {
  title?: string;
  minimizeChrome?: boolean;
  pageKey?: string;
  breadcrumb?: BreadcrumbType;
  pathElementForTitle?: string;
  contentFullHeightOverride?: boolean;
  workspaceNavBarTab?: string;
}

export const routeDataStore = atom<RouteDataStore>({});

export interface AuthStore {
  authLoaded: boolean;
  isSignedIn: boolean;
  auth?: AuthContextProps;
}

export const authStore = atom<AuthStore>({
  authLoaded: false,
  isSignedIn: false,
});

export interface CdrVersionStore {
  tiers?: Array<CdrVersionTier>;
}

export const cdrVersionStore = atom<CdrVersionStore>({});

export interface ProfileStore {
  profile?: Profile;
  load: Function;
  reload: Function;
  updateCache: Function;
}

export const profileStore = atom<ProfileStore>({
  profile: null,
  load: async () => {
    if (!profileStore.get().profile) {
      await profileStore.get().reload();
    }
    return profileStore.get().profile;
  },
  reload: async () => {
    const newProfile = await profileApi().getMe();
    profileStore.get().updateCache(newProfile);
    return profileStore.get().profile;
  },
  updateCache: (p: Profile): void =>
    profileStore.set({
      ...profileStore.get(),
      profile: p,
    }),
});

export interface CreateNewUserSatisfactionSurveyStore {
  newUserSatisfactionSurveyData: CreateNewUserSatisfactionSurvey;
}

export const createNewUserSatisfactionSurveyStore =
  atom<CreateNewUserSatisfactionSurveyStore>({
    newUserSatisfactionSurveyData: {
      satisfaction: undefined,
      additionalInfo: '',
    },
  });

export interface NotificationStore {
  title: string;
  message: string;
  showBugReportLink?: boolean;
  onDismiss?: () => void;
}

export const notificationStore = atom<NotificationStore | null>(null);

export interface StackdriverErrorReporterStore {
  reporter?: StackdriverErrorReporter;
}

export const stackdriverErrorReporterStore =
  atom<StackdriverErrorReporterStore>({});

export interface ServerConfigStore {
  config?: ConfigResponse;
  isDown?: boolean;
}

export const serverConfigStore = atom<ServerConfigStore>({});

// These fields come from the colon-prefixed params declared on router paths.
// See app-routing, signed-in-app-routing, and workspace-app-routing for examples.
// If you want to add a new route param, you will need to define it here as well.
export interface MatchParams {
  cid?: string;
  crid?: string;
  csid?: string;
  dataSetId?: string;
  domain?: string;
  eventId?: string;
  institutionId?: string;
  nbName?: string;
  ns?: string;
  pid?: string;
  sparkConsolePath?: string;
  ufid?: string;
  username?: string;
  usernameWithoutGsuiteDomain?: string;
  terraName?: string;
  appType?: string;
}

/**
 * HOC which invalidates a component when the specified params change, by way
 * of changing the React key value. This can be used to force a remount / render
 * when a route param changes.
 */
export function withParamsKey(...paramNames: (keyof MatchParams)[]) {
  return (WrappedComponent) =>
    ({ ...props }) => {
      const params = useParams<MatchParams>();
      return (
        <WrappedComponent
          key={paramNames.map((k) => params[k] || '').join('/')}
          {...props}
        />
      );
    };
}

/**
 * @name useStore
 * @description React hook that will trigger a render when the corresponding store's value changes
 *              this should only be used in function components
 * @param {Atom<T>} theStore A container for the value to be updated
 */
export function useStore<T>(theStore: Atom<T>) {
  const [value, setValue] = useState(theStore.get());
  useEffect(() => {
    return theStore.subscribe((v) => setValue(v)).unsubscribe;
  }, [theStore]);
  return value;
}

/**
 * HOC that injects the value of the given store as a prop. When the store changes, the wrapped
 * component will re-render
 */
export const withStore =
  <T,>(theStore: Atom<T>, name: string) =>
  (WrappedComponent) => {
    return (props) => {
      const value: T = useStore(theStore);
      const storeProp = { [name]: value };
      return <WrappedComponent {...props} {...storeProp} />;
    };
  };
